import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Modal, Form } from "react-bootstrap";
import "../componentCss/webform.css";
import "../componentCss/sidebar.css";
import toast, { Toaster } from 'react-hot-toast';
import "react-toastify/dist/ReactToastify.css";
import Dashboardheader from "./dashboardheader";
import { Link } from "react-router-dom";
import config from "../../config/config";
import Sidebar from "./sidebar";

import DataTable from "react-data-table-component";
import {
  getStaffListAction,
  getFormListWithPermissionAction,
  updateFormsPermissionAction,
  getFormDataListAction,
  assignPatientAction,
  getAssignedPatientListAction,
  getAllPatientsCredentialsAction,
  completePatientRegistrationAction
} from "../../Action/modernAdminActions";
import { useMigrationHelper } from '../../utils/migrationHelper';
import ReactToPrint, { useReactToPrint } from "react-to-print";
import { MdEdit } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { BsPersonAdd } from "react-icons/bs";
import { IoPerson, IoPrintSharp } from "react-icons/io5";
import { decryptData } from './decrypt';
import { useLocation } from "react-router-dom";
import { completeRegistrationSchema } from '../../validations/patientSchema';

// Complete Registration Form Component
const CompleteRegistrationForm = ({ patient, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    blood_group: 'unknown',
    height: '',
    weight: '',
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_subscriber_name: '',
    assigned_services: [],
    primary_physician_id: '',
    primary_nurse_id: '',
    known_allergies: [],
    current_medications: [],
    medical_conditions: [],
    is_vip: false,
    is_tourist: false
  });

  // Pre-populate form with existing patient data
  useEffect(() => {
    if (patient) {
      setFormData(prev => ({
        ...prev,
        // Pre-populate medical information if available
        blood_group: patient.medical_record?.blood_group || patient.blood_group || 'unknown',
        height: patient.medical_record?.height || patient.height || '',
        weight: patient.medical_record?.weight || patient.weight || '',
        
        // Pre-populate insurance information if available  
        insurance_provider: patient.insurance?.provider || patient.insurance_provider || '',
        insurance_policy_number: patient.insurance?.policy_number || patient.insurance_policy_number || '',
        insurance_subscriber_name: patient.insurance?.subscriber_name || patient.insurance_subscriber_name || '',
        
        // Pre-populate medical history if available
        known_allergies: patient.medical_record?.known_allergies || patient.known_allergies || [],
        current_medications: patient.medical_record?.current_medications || patient.current_medications || [],
        medical_conditions: patient.medical_record?.medical_conditions || patient.medical_conditions || [],
        assigned_services: patient.assigned_services || patient.planned_services || [],

        // Pre-populate staff assignments if available
        primary_physician_id: patient.primary_physician_id || '',
        primary_nurse_id: patient.primary_nurse_id || '',

        // Pre-populate patient classification flags
        is_vip: patient.demographics?.is_vip || patient.is_vip || false,
        is_tourist: patient.demographics?.is_tourist || patient.is_tourist || false
      }));
    }
  }, [patient]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayInputChange = (e, field) => {
    const { value } = e.target;
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate the form data using Yup schema
      await completeRegistrationSchema.validate(formData, { abortEarly: false });
      onSubmit(formData);
    } catch (validationError) {
      if (validationError.inner) {
        // Show first validation error
        toast.error(validationError.inner[0].message);
      } else {
        toast.error(validationError.message || 'Please check all required fields');
      }
    }
  };

  return (
    <div>
      <div className="mb-3">
        <h6>Patient Information</h6>
        <p><strong>Name:</strong> {patient.full_name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim()}</p>
        <p><strong>Temporary MRN:</strong> {patient.mrn_temporary}</p>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Form.Group as={Col} md={4}>
            <Form.Label>Blood Group</Form.Label>
            <Form.Select
              name="blood_group"
              value={formData.blood_group}
              onChange={handleInputChange}
            >
              <option value="unknown">Select Blood Group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </Form.Select>
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label>Height (cm)</Form.Label>
            <Form.Control
              type="number"
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              placeholder="Enter height in cm"
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label>Weight (kg)</Form.Label>
            <Form.Control
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              placeholder="Enter weight in kg"
            />
          </Form.Group>
        </Row>

        <h6 className="mb-3">Insurance Information</h6>
        <Row className="mb-3">
          <Form.Group as={Col} md={4}>
            <Form.Label>Insurance Provider</Form.Label>
            <Form.Control
              type="text"
              name="insurance_provider"
              value={formData.insurance_provider}
              onChange={handleInputChange}
              placeholder="Enter insurance provider"
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label>Policy Number</Form.Label>
            <Form.Control
              type="text"
              name="insurance_policy_number"
              value={formData.insurance_policy_number}
              onChange={handleInputChange}
              placeholder="Enter policy number"
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label>Subscriber Name</Form.Label>
            <Form.Control
              type="text"
              name="insurance_subscriber_name"
              value={formData.insurance_subscriber_name}
              onChange={handleInputChange}
              placeholder="Enter subscriber name"
            />
          </Form.Group>
        </Row>

        <h6 className="mb-3">Medical Information</h6>
        <Row className="mb-3">
          <Form.Group as={Col} md={6}>
            <Form.Label>Known Allergies (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              value={formData.known_allergies.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'known_allergies')}
              placeholder="e.g., Penicillin, Latex, Peanuts"
            />
          </Form.Group>

          <Form.Group as={Col} md={6}>
            <Form.Label>Current Medications (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              value={formData.current_medications.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'current_medications')}
              placeholder="e.g., Aspirin, Insulin, Metformin"
            />
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} md={12}>
            <Form.Label>Medical Conditions (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              value={formData.medical_conditions.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'medical_conditions')}
              placeholder="e.g., Diabetes, Hypertension, Asthma"
            />
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} md={6}>
            <Form.Label>Primary Physician ID</Form.Label>
            <Form.Control
              type="text"
              name="primary_physician_id"
              value={formData.primary_physician_id}
              onChange={handleInputChange}
              placeholder="Enter physician ID"
            />
          </Form.Group>

          <Form.Group as={Col} md={6}>
            <Form.Label>Primary Nurse ID</Form.Label>
            <Form.Control
              type="text"
              name="primary_nurse_id"
              value={formData.primary_nurse_id}
              onChange={handleInputChange}
              placeholder="Enter nurse ID"
            />
          </Form.Group>
        </Row>

        <Row className="mb-4">
          <Form.Group as={Col} md={12}>
            <Form.Label>Assigned Services (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              value={formData.assigned_services.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'assigned_services')}
              placeholder="e.g., Dialysis, Cardiology, Neurology"
            />
          </Form.Group>
        </Row>

        <h6 className="mb-3 mt-4">Patient Classification</h6>
        <Row className="mb-3">
          <Form.Group as={Col} md={6}>
            <Form.Check
              type="checkbox"
              name="is_vip"
              label="VIP Patient"
              checked={formData.is_vip}
              onChange={handleCheckboxChange}
            />
            <Form.Text className="text-muted">
              Mark this patient as VIP for priority treatment
            </Form.Text>
          </Form.Group>

          <Form.Group as={Col} md={6}>
            <Form.Check
              type="checkbox"
              name="is_tourist"
              label="Tourist/Visitor"
              checked={formData.is_tourist}
              onChange={handleCheckboxChange}
            />
            <Form.Text className="text-muted">
              Mark this patient as a tourist or temporary visitor
            </Form.Text>
          </Form.Group>
        </Row>

        <Row>
          <Form.Group as={Col}>
            {isLoading ? (
              <button
                disabled
                className="btn btn-primary"
              >
                Completing Registration...{" "}
                <img
                  height="20"
                  width="20px"
                  src="assets/images/loading.gif"
                  alt="Loading"
                />
              </button>
            ) : (
              <>
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                >
                  Complete Registration
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </>
            )}
          </Form.Group>
        </Row>
      </Form>
    </div>
  );
};

const PatientCredentials = () => {
  const { useModernAPI } = useMigrationHelper();
  const location = useLocation();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isAccessAllPatient, setIsAccessAllPatient] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [formList, setFormList] = useState([]);
  const [isModelOpen, setIsOpen] = useState(false);
  const [staffId, setStaffId] = useState(0);
  const [isLaoding, setisLaoding] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isModelView, setIsModelView] = useState(false);
  const [isModelAssignPatient, setIsModelAssignPatient] = useState(false);
  const [viewData, setViewData] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [details, setDetails] = useState();
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isCompleteRegistrationModal, setIsCompleteRegistrationModal] = useState(false);
  const [selectedPatientForRegistration, setSelectedPatientForRegistration] = useState(null);
  const [isCompletingRegistration, setIsCompletingRegistration] = useState(false);
  const [filterVip, setFilterVip] = useState('all');
  const [filterTourist, setFilterTourist] = useState('all');
  const allDataRef = useRef();
  const singleRowRef = useRef();

  const handlePageChange = (page) => {
    setCurrentPage(page - 1);
  };

  const handleCheckboxChange = (event) => {
    const value = event.target.value;
    setSelectedOptions(prev =>
      prev.includes(value) ? prev.filter(option => option !== value) : [...prev, value]
    );
  };

  const handleCheckboxChangeForAllPatient = (event) => {
    const value = event.target.value;
    setIsAccessAllPatient(isAccessAllPatient == 1 ? 0 : 1)
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
  };
  useEffect(() => {
    getStaffListAPI();
    getPatientsList();
    
    // Handle refresh state from navigation
    if (location.state?.refresh) {
      // Show success message if provided
      if (location.state.message) {
        toast.success(location.state.message);
      }
      
      // Clear the navigation state to prevent repeated notifications
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    setSelectedRow(null);
  }, [currentPage]);

  // Trigger patient list refresh when filters change
  useEffect(() => {
    if (!isLoading) {
      getPatientsList();
    }
  }, [filterVip, filterTourist]);

  // Debug effect to log patientList changes
  useEffect(() => {
  }, [patientList]);

  let handleChange = (permissionType, formId) => {
    setFormList((prevForms) => {
      return prevForms.map((form) => {
        if (form.id === formId) {
          if (permissionType == "isAccess") {
            return {
              ...form,
              isAccess: !form.isAccess,
            };
          } else if (permissionType == "isEdit") {
            return {
              ...form,
              isEdit: !form.isEdit,
            };
          } else if (permissionType == "isDelete") {
            return {
              ...form,
              isDelete: !form.isDelete,
            };
          }
        }
        return form;
      });
    });
  };

  const handleClose = () => setIsOpen(false);
  const handleOpen = (id) => {
    setStaffId(id);
    getFormListAPI(id);
    setIsOpen(true);
  };

  const handleViewClose = () => setIsModelView(false);
  const handleOpenView = (data) => {
    setViewData(data);
    setIsModelView(true);
  };

  const handleOpenAssignPatient = async (data) => {
    let res = await getFormDataListAction({ 'id': 1 });
    if (res.success) {
      let data = decryptData(res.data?.records)
      setPatientList(data);
    }

    let assignedPatientList = await getAssignedPatientListAction({ 'staffId': data.id });
    if (assignedPatientList.success) {
      let data = decryptData(assignedPatientList.data)
      let recordIds = data.map(item => item.recordId.toString());
      setSelectedOptions(recordIds);
    }

    setViewData(data);
    setIsModelAssignPatient(true);
    setIsAccessAllPatient(data.isAccessAllPatient)
  };
  const handleViewCloseAssignPatient = () => {
    setIsModelAssignPatient(false);
    setSelectedOptions([]);
  }

  // Complete Registration Handlers
  const handleOpenCompleteRegistration = (patient) => {
    setSelectedPatientForRegistration(patient);
    setIsCompleteRegistrationModal(true);
  };

  const handleCloseCompleteRegistration = () => {
    setIsCompleteRegistrationModal(false);
    setSelectedPatientForRegistration(null);
  };

  const handleCompleteRegistration = async (formData) => {
    setIsCompletingRegistration(true);
    try {
      const registrationData = {
        mrn_temporary: selectedPatientForRegistration.mrn_temporary,
        blood_group: formData.blood_group,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        insurance_provider: formData.insurance_provider,
        insurance_policy_number: formData.insurance_policy_number,
        insurance_subscriber_name: formData.insurance_subscriber_name,
        assigned_services: formData.assigned_services || [],
        primary_physician_id: formData.primary_physician_id,
        primary_nurse_id: formData.primary_nurse_id,
        known_allergies: formData.known_allergies || [],
        current_medications: formData.current_medications || [],
        medical_conditions: formData.medical_conditions || [],
        is_vip: formData.is_vip || false,
        is_tourist: formData.is_tourist || false
      };

      const response = await completePatientRegistrationAction(registrationData);
      
      if (response.success || response.id) {
        toast.success('Patient registration completed successfully!');
        handleCloseCompleteRegistration();
        
        // Refresh the patient list to show updated status
        setTimeout(() => {
          getPatientsList();
        }, 500);
        
        // Update the patient in the current list if possible
        setPatientList(prevList => 
          prevList.map(p => 
            p.mrn_temporary === selectedPatientForRegistration.mrn_temporary
              ? { ...p, registration_status: 'completed', mrn_permanent: response.mrn_permanent }
              : p
          )
        );
      } else {
        toast.error(response.message || 'Failed to complete registration');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete registration');
    } finally {
      setIsCompletingRegistration(false);
    }
  };

  let getFormListAPI = async (userId) => {
    let res = await getFormListWithPermissionAction({ userId: userId });
    if (res.success) {
      setFormList(decryptData(res.data));
    }
  };

  let getStaffListAPI = async () => {
    let res = await getStaffListAction(details);
    setIsLoading(false);
    if (res.success) {
      let originalText = decryptData(res.data);
      setStaffList(originalText);
    }
  };
  let getPatientsList = async () => {
    const params = {};

    // Add VIP filter if set
    if (filterVip !== 'all') {
      params.is_vip = filterVip === 'yes';
    }

    // Add Tourist filter if set
    if (filterTourist !== 'all') {
      params.is_tourist = filterTourist === 'yes';
    }

    let res = await getAllPatientsCredentialsAction(params);

    setIsLoading(false);

    // The backend returns the data directly without a success wrapper
    // res should contain { patients: [...], total: 1, page: 1, ... }
    if (res && res.patients) {
      setPatientList(res.patients);
    } else if (res && Array.isArray(res)) {
      // Fallback: if res is directly an array
      setPatientList(res);
    } else {
      setPatientList([]);
    }
  };

  let getStaffListAPIForFilter = async () => {
    setDetails("");
    setFilterVip('all');
    setFilterTourist('all');
    let res = await getStaffListAction();

    if (res.success) {
      setStaffList(res.data);
    }
  };

  const searchByNameHandler = (e) => {
    const { name, value } = e.target;
    setDetails((old) => {
      return { ...old, [name]: value };
    });
  };
  let submitPermissionForm = async (e) => {
    e.preventDefault();
    setisLaoding(1);
    let res = await updateFormsPermissionAction({
      userId: staffId,
      formsPermission: formList,
    });
    if (res.success) {
      toast.success(res.msg);
      setTimeout(() => {
        setisLaoding(0);
        setIsOpen(false);
      }, 2000);
    } else {
      toast.error(res.msg);
    }
  };

  let assignPatientSubmit = async (e) => {
    e.preventDefault();
    if (selectedOptions.length == 0) {
      toast.error('Please select patient.');
      return;
    }
    let res = await assignPatientAction({ staffId: viewData.id, patientIds: selectedOptions, 'isAccessAllPatient': isAccessAllPatient });
    if (res.success) {
      toast.success(res.msg);
      setIsModelAssignPatient(false);
      setSelectedOptions([]);
    } else {
      toast.error(res.msg);
    }
  };

  const columns = [
    {
      name: "#",
      cell: (row, index, column, id) => {
        return index + 1 + currentPage * rowsPerPage;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Patient Name",
      selector: (row) => row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
      sortable: true,
      width: "12%"
    },
    {
      name: "MRN Number",
      selector: (row) => row.current_mrn || row.mrn || row.MRNno || 'N/A',
      sortable: true,
      width: "10%"
    },
    {
      name: "Age",
      selector: (row) => row.age || 'N/A',
      sortable: true,
      width: "8%"
    },
    {
      name: "Gender",
      selector: (row) => row.gender || 'N/A',
      sortable: true,
      width: "8%"
    },
    {
      name: "Registration Status",
      cell: (row) => {
        const isPreRegistered = row.registration_status === 'pre_registered' || 
                               (row.mrn_temporary && !row.mrn_permanent);
        return (
          <span className={`badge ${isPreRegistered ? 'bg-warning' : 'bg-success'}`}>
            {isPreRegistered ? 'Pre-Registered' : 'Registered'}
          </span>
        );
      },
      sortable: true,
      width: "12%"
    },
    {
      name: "Flags",
      cell: (row) => {
        const isVip = row.demographics?.is_vip || row.is_vip;
        const isTourist = row.demographics?.is_tourist || row.is_tourist;

        return (
          <div className="d-flex gap-1">
            {isVip && (
              <span className="badge bg-warning text-dark" title="VIP Patient">
                VIP
              </span>
            )}
            {isTourist && (
              <span className="badge bg-info" title="Tourist/Visitor">
                Tourist
              </span>
            )}
            {!isVip && !isTourist && <span className="text-muted">-</span>}
          </div>
        );
      },
      sortable: false,
      width: "10%"
    },
    {
      name: "Status",
      selector: (row) =>
        row.status === "active" || row.status === 1 ? (
          <>
            <div style={{ width: "auto" }}>
              <p className="text-primary mb-0">Active</p>
            </div>
          </>
        ) : (
          <>
            <div style={{ width: "auto" }}>
              <span className="text-danger">Inactive</span>
            </div>
          </>
        ),
      sortable: true,
      width: "10%"
    },
    {
      name: "Action",
      cell: (row) => {
        const isPreRegistered = row.registration_status === 'pre_registered' || 
                               (row.mrn_temporary && !row.mrn_permanent);
        return (
          <>
            <Link to={`${config.baseUrl}edit-patientCredentials/${row.id}`} title="Edit">
              <span><MdEdit fill='#434544' size={20} /></span>
            </Link>
            &nbsp;
            {isPreRegistered && (
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleOpenCompleteRegistration(row)}
                title="Complete Registration"
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                Complete
              </button>
            )}
          </>
        );
      },
      sortable: false,
      width: "18%"
    },
  ];

  const printColumns = [
    {
      name: "#",
      cell: (row, index, column, id) => {
        return index + 1 + currentPage * 10;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Mobile Number",
      selector: (row) => row.mobileNumber,
      sortable: true,
    },
    {
      name: "Role",
      selector: (row) => row.userGroup,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) =>
        row.isActive == 1 ? (
          <p className="text-primary">Active</p>
        ) : (
          <span className="text-danger">Inactive</span>
        ),
      sortable: true,
    },
  ];

  const printAllData = useReactToPrint({
    content: () => allDataRef.current,
  });

  const handlePrint = (row) => {
    setSelectedRow(row);
  };

  const printSingleRow = useReactToPrint({
    content: () => singleRowRef.current,
  });

  useEffect(() => {
    if (selectedRow) {
      printSingleRow();
    }
    setSelectedRow(null);
  }, [selectedRow, printSingleRow]);

  return (
    <>
      <Dashboardheader />
      <Toaster />
      <div className="wrapper">
        <Sidebar />
        <div id="content">
          <Container fluid className="p-4">
            <Row>
              <Col lg={12} md={12} sm={12} className="">
                <div className="innerhead mb-4">
                  <h5 className="headtitle">Patient Credentials</h5>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12} className="mt-3">
                <div style={{ float: "right" }}>
                  <Link to={`${config.baseUrl}add-patientCredentials`}>
                    <button className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6 mt-4">
                      Add+
                    </button>

                  </Link>
                </div>
                <div className="">
                  <div className="row">
                    <div className="col-md-3">
                      <label htmlFor="name">Search By:</label>&nbsp;
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        onChange={searchByNameHandler}
                      />
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="cars">Search By Status</label> &nbsp; &nbsp;
                      <select
                        name="status"
                        id="cars"
                        className="form-control"
                        onChange={searchByNameHandler}
                      >
                        <option value="1">Active</option>
                        <option value="0">In active</option>
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="vipFilter">Filter by VIP Status</label>
                      <select
                        name="vipFilter"
                        id="vipFilter"
                        className="form-control"
                        value={filterVip}
                        onChange={(e) => setFilterVip(e.target.value)}
                      >
                        <option value="all">All Patients</option>
                        <option value="yes">VIP Only</option>
                        <option value="no">Non-VIP Only</option>
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label htmlFor="touristFilter">Filter by Tourist Status</label>
                      <select
                        name="touristFilter"
                        id="touristFilter"
                        className="form-control"
                        value={filterTourist}
                        onChange={(e) => setFilterTourist(e.target.value)}
                      >
                        <option value="all">All Patients</option>
                        <option value="yes">Tourists Only</option>
                        <option value="no">Non-Tourists Only</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <button
                        className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6 mt-4"
                        onClick={getStaffListAPIForFilter}
                      >
                        Reset
                      </button>
                      &nbsp;
                      <button
                        className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6 mt-4"
                        onClick={getStaffListAPI}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
                <br />
                <ReactToPrint
                  trigger={() => <button className='px-3 py-2 btn-sm btn-secondary-btn3 fs-6 no-print'>Print</button>}
                  content={printAllData}
                />
                <div className="maintable table-responsive">
                  <h5 className="">Patient List </h5>
                  {isLoading ? (
                    <div style={{ textAlign: "center" }}>
                      <img
                        style={{ width: "40px" }}
                        src="assets/images/loading.gif"
                      />
                    </div>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={patientList}
                      pagination
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 15, 25, 50, 100]}
                      paginationComponentOptions={{
                        rowsPerPageText: 'Row per page:',
                        rangeSeparatorText: 'out of',
                      }}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handleRowsPerPageChange}
                    />

                  )}
                </div>
                {/*------------------------------- Section For Showing All Data For Print--------------------------------------------------- */}

                <div style={{ display: 'none' }}>
                  <div ref={allDataRef}>
                    <center>
                      <h2 className=''> Staff Management List</h2>
                    </center>
                    <DataTable
                      columns={printColumns}
                      data={staffList}
                      pagination={false}
                    />
                  </div>
                </div>
                {/*------------------------------- Section For Showing Single Data For Print--------------------------------------------------- */}

                <div style={{ display: 'none' }}>
                  <div className="card-body" ref={singleRowRef} style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '10px', margin: '20px', fontFamily: 'Arial, sans-serif' }}>
                    {selectedRow && (
                      <div style={{ textAlign: 'left', lineHeight: '1.5' }}>
                        <h2 className="card-title" style={{ textAlign: 'center', marginBottom: '20px' }}>Staff Management List</h2>
                        <p><strong>ID:</strong> {selectedRow.id}</p>
                        <p><strong>First Name:</strong> {selectedRow.firstName}</p>
                        <p><strong>Last Name:</strong> {selectedRow.lastName}</p>
                        <p><strong>City:</strong> {selectedRow.city}</p>
                        <p><strong>Address:</strong> {selectedRow.address}</p>
                        <p><strong>Designation:</strong> {selectedRow.designation}</p>
                        <p><strong>Employee Id:</strong> {selectedRow.employeeId}</p>
                        <p><strong>Gender:</strong> {selectedRow.gender}</p>
                        <p><strong>Date Of Joining:</strong> {selectedRow.DateOfJoining}</p>
                        <p><strong>Licence Number:</strong> {selectedRow.licenceNumber}</p>
                        <p><strong>Mobile Number:</strong> {selectedRow.mobileNumber}</p>
                        <p><strong>User Group:</strong> {selectedRow.userGroup}</p>
                        <p><strong>Image:</strong>
                          {selectedRow.photo ? (
                            <>
                              <img width="100" src={selectedRow.photo}></img>
                              <br />
                            </>
                          ) : (
                            ""
                          )}</p>
                        <p><strong>Signature:</strong>
                          {selectedRow.signature ? (
                            <>
                              <img width="100" src={selectedRow.signature}></img>
                              <br />
                            </>
                          ) : (
                            ""
                          )}
                        </p>
                        <p><strong>Stamp Image:</strong>
                          {selectedRow.stampImage ? (
                            <>
                              <img width="100" src={selectedRow.stampImage}></img>
                              <br />
                            </>
                          ) : (
                            ""
                          )}
                        </p>
                        <p><strong>Status:</strong> {selectedRow.status == 1 ? 'Active' : 'Inactive'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>


            <Modal
              show={isModelOpen}
              onHide={() => handleClose(false)}
              dialogClassName="modal-90w modal-right"
              aria-labelledby="example-custom-modal-styling-title"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <b>Permissions</b>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Row className="mb-4">
                    <div className="col-md-6">
                      <Form.Label>
                        <b> Form Name </b>
                      </Form.Label>{" "}
                      <br />
                    </div>

                    <div className="col-md-6">
                      <Form.Label>
                        <b>Access</b>
                      </Form.Label>
                    </div>

                    {/* <div className="col-md-2">
                    <Form.Label>
                      <b> Edit </b>
                    </Form.Label>
                  </div>

                  <div className="col-md-2">
                    <Form.Label>
                      {" "}
                      <b> Delete </b>
                    </Form.Label>
                  </div> */}
                  </Row>
                  <hr />

                  {formList.length > 0
                    ? formList.map((data) => (
                      <>
                        <Row className="mb-4">
                          <div className="col-md-6">{data.name}</div>

                          <div className="col-md-6">
                            <input
                              type="checkbox"
                              name="isAccess"
                              onChange={() =>
                                handleChange("isAccess", data.id)
                              }
                              checked={data.isAccess == 1 ? "checked" : ""}
                            />
                          </div>

                          {/* <div className="col-md-2">
                          <input
                            type="checkbox"
                            name="isEdit"
                            onChange={() => handleChange("isEdit", data.id)}
                            checked={data.isEdit == 1 ? "checked" : ""}
                          />
                        </div>

                        <div className="col-md-2">
                          <input
                            type="checkbox"
                            name="isDelete"
                            onChange={() =>
                              handleChange("isDelete", data.id)
                            }
                            checked={data.isDelete == 1 ? "checked" : ""}
                          />
                        </div> */}
                        </Row>
                        <hr />
                      </>
                    ))
                    : ""}

                  <Row className="mb-4">
                    <Form.Group as={Col}>
                      {isLaoding ? (
                        <>
                          <button
                            disabled
                            className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6"
                          >
                            Loading{" "}
                            <img
                              height="20"
                              width="20px"
                              src="assets/images/loading.gif"
                            />
                          </button>
                        </>
                      ) : (
                        <button
                          type="submit"
                          onClick={submitPermissionForm}
                          className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6"
                        >
                          Submit
                        </button>
                      )}
                    </Form.Group>
                  </Row>
                </Form>
              </Modal.Body>
            </Modal>

            {/* Assign form to staff */}
            <Modal
              show={isModelView}
              onHide={() => handleViewClose(false)}
              dialogClassName="modal-90w modal-right modal-lg"
              aria-labelledby="example-custom-modal-styling-title"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <b>View Staff Details</b>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>

                <ul className="list-group">
                  <li className="list-group-item d-flex flex-row " >
                    <div className="w-50">
                      <strong className="staffView w-50">First Name:</strong>{" "}
                    </div>

                    {viewData?.namePrefix}{" "}
                    {viewData?.firstName ? viewData?.firstName : "NA"}
                  </li>
                  <li className="list-group-item d-flex flex-row "  >
                    <div className="w-50">
                      <strong className="staffView">Middle Name:</strong>{" "}
                    </div>

                    {viewData?.middleName ? viewData?.middleName : "NA"}
                  </li>
                  <li className="list-group-item d-flex flex-row">

                    <div className="w-50">
                      <strong className="staffView">Last Name:</strong>{" "}
                    </div>

                    {viewData?.lastName ? viewData?.lastName : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Employee ID:</strong>{" "}
                    </div>

                    {viewData?.employeeId ? viewData?.employeeId : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Email:</strong>{" "}
                    </div>

                    {viewData?.email ? viewData?.email : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Mobile Number:</strong>{" "}
                    </div>

                    {viewData?.mobileNumber ? viewData?.mobileNumber : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">City:</strong>{" "}
                    </div>

                    {viewData?.city ? viewData?.city : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Address:</strong>{" "}
                    </div>

                    {viewData?.address ? viewData?.address : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">State:</strong>{" "}
                    </div>
                    {viewData?.state ? viewData?.state : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Emirates ID number:</strong>{" "}
                    </div>
                    {viewData?.emiratesIdNumber
                      ? viewData?.emiratesIdNumber
                      : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Gender:</strong>{" "}
                    </div>
                    {viewData?.gender == 1
                      ? "Male"
                      : viewData?.gender == 2
                        ? "Female"
                        : "Other"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Designation:</strong>{" "}
                    </div>
                    {viewData?.designation ? viewData?.designation : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Role:</strong>{" "}
                    </div>

                    {viewData?.userGroup ? viewData?.userGroup : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Stamp Image:</strong>
                    </div>
                    {viewData.stampImage ? (
                      <>
                        <img width="100" src={viewData.stampImage}></img>
                        <br />
                      </>
                    ) : (
                      ""
                    )}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Photo:</strong>
                    </div>
                    {viewData.photo ? (
                      <>
                        <img width="100" src={viewData.photo}></img>
                        <br />
                      </>
                    ) : (
                      ""
                    )}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Signature:</strong>
                    </div>
                    {viewData.signature ? (
                      <>
                        <img width="100" src={viewData.signature}></img>
                        <br />
                      </>
                    ) : (
                      ""
                    )}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">Date of joining:</strong>
                    </div>
                    {viewData?.DateOfJoining ? viewData?.DateOfJoining : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row">
                    <div className="w-50">
                      <strong className="staffView">License Number:</strong>{" "}
                    </div>

                    {viewData?.licenceNumber ? viewData?.licenceNumber : "NA"}
                  </li>
                </ul>
              </Modal.Body>
            </Modal>

            {/* Assign patient to staff */}
            <Modal
              show={isModelAssignPatient}
              onHide={() => handleViewCloseAssignPatient(false)}
              dialogClassName="modal-90w modal-right modal-lg"
              aria-labelledby="example-custom-modal-styling-title"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <b>Assign Patient</b>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>

                <ul className="list-group">
                  <li className="list-group-item d-flex flex-row " >
                    <div className="w-50">
                      <strong className="staffView w-50">Staff Name:</strong>{" "}
                    </div>

                    {viewData?.namePrefix}{" "}
                    {viewData?.firstName ? viewData?.firstName : "NA"}
                  </li>

                  <li className="list-group-item d-flex flex-row " >
                    <div className="w-50">
                      <strong className="staffView w-50">Is access all patient:</strong>
                    </div>
                    <input
                      type="checkbox"
                      value="1"
                      checked={isAccessAllPatient}
                      onChange={handleCheckboxChangeForAllPatient}
                    />
                  </li>

                  {isAccessAllPatient == 0 &&
                    <>
                      <li className="list-group-item d-flex flex-row " >
                        <div className="w-50">
                          <strong className="staffView w-50">Patient List:</strong>{" "}
                        </div>
                        <ul style={{ listStyleType: 'none', padding: 0, height: '300px', overflowX: 'auto' }}>
                          {patientList.map(data => (
                            <li key={data.recordId}>
                              <label>
                                <input
                                  type="checkbox"
                                  value={data.recordId}
                                  checked={selectedOptions.includes(data.recordId)}
                                  onChange={handleCheckboxChange}
                                />
                                &nbsp; {`${data[1] ? data[1] : ''} ${data[2] ? data[2] : ''}(${data[1929] ? data[1929] : 'NA'})`}
                              </label>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </>
                  }
                  <li className="list-group-item d-flex flex-row " >
                    <div className="w-50">
                      <button className="px-3 py-2 btn-sm btn-secondary-btn3 fs-6" onClick={assignPatientSubmit}>Assign</button>
                    </div>
                  </li>
                </ul>
              </Modal.Body>
            </Modal>

            {/* Complete Registration Modal */}
            <Modal
              show={isCompleteRegistrationModal}
              onHide={handleCloseCompleteRegistration}
              dialogClassName="modal-90w modal-right modal-lg"
              aria-labelledby="complete-registration-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  <b>Complete Patient Registration</b>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedPatientForRegistration && (
                  <CompleteRegistrationForm
                    patient={selectedPatientForRegistration}
                    onSubmit={handleCompleteRegistration}
                    onCancel={handleCloseCompleteRegistration}
                    isLoading={isCompletingRegistration}
                  />
                )}
              </Modal.Body>
            </Modal>
          </Container>
        </div>
      </div>
    </>
  );
};

export default PatientCredentials;
